import { createAppRequest } from '@/services/request';
import { buildBusinessTripItemListPath } from './business-trip-item-scope-query';
import type { ApiRequestOptions } from './types/common';
import type {
  AddTripPlanMembersPayload,
  AddTripActivityMembersPayload,
  AddTripPlanItemsPayload,
  BusinessTripItem,
  CrmObjectType,
  CrmReferencePage,
  BusinessTripItemActivity,
  CompleteTripPlanPayload,
  CreateBusinessTripItemPayload,
  CreateTripPlanPayload,
  RemoveTripPlanItemPayload,
  ReorderTripPlanItemsPayload,
  TripPlan,
  TripPlanActivityPage,
  TripPlanRecycleBinItem,
  TripActivityDetail,
  TripActivityMember,
  TripPlanDetail,
  TripPlanItinerary,
  TripPlanListQuery,
  TripPlanListResult,
  TripAnalyticsPeriod,
  TripAnalyticsResult,
  TripItemListQuery,
  TripItemListResult,
  TaxonomyCityOption,
  TenantMember,
  TripWeatherResult,
  TripWeatherComparisonQuery,
  TripWeatherComparisonResult,
  UpdateBusinessTripItemPayload,
  UpdateBusinessTripItemStatusPayload,
  UpdateTripPlanCandidateDecisionPayload,
  UpdateTripPlanDayNotePayload,
  UpdateTripPlanMemberPermissionPayload,
  UpdateTripActivityMemberPermissionPayload,
  UpdateTripPlanPayload,
  UpdateTripPlanItemPayload,
  WorkbenchData,
} from './types/trip';

const request = createAppRequest();
const TRIP_PREFIX = '/bff/business-trip';

export async function searchCrmReferences(
  type: CrmObjectType,
  query?: { keyword?: string; page?: number; pageSize?: number },
  options?: ApiRequestOptions,
) {
  return request<CrmReferencePage>(`${TRIP_PREFIX}/crm/references`, {
    method: 'GET',
    params: { type, ...query },
    ...(options || {}),
  });
}

export async function getWorkbench(options?: ApiRequestOptions) {
  return request<WorkbenchData>(`${TRIP_PREFIX}/workbench`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTripAnalytics(
  period: TripAnalyticsPeriod,
  options?: ApiRequestOptions,
) {
  return request<TripAnalyticsResult>(`${TRIP_PREFIX}/analytics`, {
    method: 'GET',
    params: { period },
    ...(options || {}),
  });
}

export async function listTaxonomyCities(options?: ApiRequestOptions) {
  return request<TaxonomyCityOption[]>(`${TRIP_PREFIX}/taxonomy/cities`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function listBusinessTripItems(
  query?: TripItemListQuery,
  options?: ApiRequestOptions,
) {
  const { cityScope, ...params } = query ?? {};
  return request<TripItemListResult>(buildBusinessTripItemListPath(cityScope), {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createBusinessTripItem(
  body: CreateBusinessTripItemPayload,
  options?: ApiRequestOptions,
) {
  return request<BusinessTripItem>(`${TRIP_PREFIX}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getBusinessTripItemDetail(
  businessTripItemUuid: string,
  options?: ApiRequestOptions,
) {
  return request<BusinessTripItem>(`${TRIP_PREFIX}/items/${businessTripItemUuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export const getBusinessTripItem = getBusinessTripItemDetail;

export async function listBusinessTripItemActivities(
  businessTripItemUuid: string,
  options?: ApiRequestOptions,
) {
  return request<BusinessTripItemActivity[]>(
    `${TRIP_PREFIX}/items/${encodeURIComponent(businessTripItemUuid)}/activities`,
    { method: 'GET', ...(options || {}) },
  );
}

export async function listBusinessTripItemMembers(businessTripItemUuid: string, options?: ApiRequestOptions) {
  return request<TripActivityMember[]>(`${TRIP_PREFIX}/items/${encodeURIComponent(businessTripItemUuid)}/members`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function addBusinessTripItemMembers(
  businessTripItemUuid: string,
  body: AddTripActivityMembersPayload,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(`${TRIP_PREFIX}/items/${encodeURIComponent(businessTripItemUuid)}/members`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}),
  });
}

export async function updateBusinessTripItemMemberPermission(
  businessTripItemUuid: string,
  memberUuid: string,
  body: UpdateTripActivityMemberPermissionPayload,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/items/${encodeURIComponent(businessTripItemUuid)}/members/${encodeURIComponent(memberUuid)}`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}) },
  );
}

export async function removeBusinessTripItemMember(
  businessTripItemUuid: string,
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/items/${encodeURIComponent(businessTripItemUuid)}/members/${encodeURIComponent(memberUuid)}`,
    { method: 'DELETE', ...(options || {}) },
  );
}

export async function deleteBusinessTripItem(
  businessTripItemUuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${TRIP_PREFIX}/items/${businessTripItemUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function updateBusinessTripItem(
  businessTripItemUuid: string,
  body: UpdateBusinessTripItemPayload,
  options?: ApiRequestOptions,
) {
  return request<BusinessTripItem>(`${TRIP_PREFIX}/items/${businessTripItemUuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function updateBusinessTripItemStatus(
  businessTripItemUuid: string,
  body: UpdateBusinessTripItemStatusPayload,
  options?: ApiRequestOptions,
) {
  return request<BusinessTripItem>(`${TRIP_PREFIX}/items/${businessTripItemUuid}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function listTripPlans(
  query?: TripPlanListQuery,
  options?: ApiRequestOptions,
) {
  return request<TripPlanListResult>(`${TRIP_PREFIX}/plans`, {
    method: 'GET',
    params: query,
    ...(options || {}),
  });
}

export async function createTripPlan(
  body: CreateTripPlanPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlan>(`${TRIP_PREFIX}/plans`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getTripPlanDetail(
  planUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function listTripPlanMembers(
  keyword?: string,
  options?: ApiRequestOptions,
) {
  return request<TenantMember[]>(`${TRIP_PREFIX}/members`, {
    method: 'GET',
    params: keyword ? { keyword } : undefined,
    ...(options || {}),
  });
}

export async function listTripActivities(options?: ApiRequestOptions) {
  return request<{ data: TripActivityDetail[]; total: number }>(`${TRIP_PREFIX}/activities`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTripActivityDetail(
  activityUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripActivityDetail>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}`,
    { method: 'GET', ...(options || {}) },
  );
}

export async function updateTripActivity(
  activityUuid: string,
  body: UpdateTripPlanItemPayload,
  options?: ApiRequestOptions,
) {
  return request<TripActivityDetail>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

export async function listTripActivityMembers(
  activityUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}/members`,
    { method: 'GET', ...(options || {}) },
  );
}

export async function addTripActivityMembers(
  activityUuid: string,
  body: AddTripActivityMembersPayload,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}/members`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

export async function updateTripActivityMemberPermission(
  activityUuid: string,
  memberUuid: string,
  body: UpdateTripActivityMemberPermissionPayload,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}/members/${encodeURIComponent(memberUuid)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

export async function removeTripActivityMember(
  activityUuid: string,
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripActivityMember[]>(
    `${TRIP_PREFIX}/activities/${encodeURIComponent(activityUuid)}/members/${encodeURIComponent(memberUuid)}`,
    { method: 'DELETE', ...(options || {}) },
  );
}

export async function addTripPlanMembers(
  planUuid: string,
  body: AddTripPlanMembersPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

export async function removeTripPlanMember(
  planUuid: string,
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(
    `${TRIP_PREFIX}/plans/${planUuid}/members/${encodeURIComponent(memberUuid)}`,
    { method: 'DELETE', ...(options || {}) },
  );
}

export async function updateTripPlanMemberPermission(
  planUuid: string,
  memberUuid: string,
  body: UpdateTripPlanMemberPermissionPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(
    `${TRIP_PREFIX}/plans/${planUuid}/members/${encodeURIComponent(memberUuid)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

export async function getTripPlanItinerary(
  planUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripPlanItinerary>(`${TRIP_PREFIX}/plans/${planUuid}/itinerary`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateTripPlanDayNote(
  planUuid: string,
  executionDate: string,
  body: UpdateTripPlanDayNotePayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanItinerary>(
    `${TRIP_PREFIX}/plans/${planUuid}/day-notes/${encodeURIComponent(executionDate)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

export async function getTripPlanWeather(
  planUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripWeatherResult>(`${TRIP_PREFIX}/plans/${planUuid}/weather`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateTripPlan(
  planUuid: string,
  body: UpdateTripPlanPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlan>(`${TRIP_PREFIX}/plans/${planUuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function completeTripPlan(
  planUuid: string,
  body: CompleteTripPlanPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

export async function deleteTripPlan(
  planUuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${TRIP_PREFIX}/plans/${planUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function listTripPlanCandidates(
  planUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/candidates`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function addTripPlanItems(
  planUuid: string,
  body: AddTripPlanItemsPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function updateTripPlanCandidateDecision(
  planUuid: string,
  businessTripItemUuid: string,
  body: UpdateTripPlanCandidateDecisionPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/candidates/${businessTripItemUuid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

export async function updateTripPlanItem(
  planUuid: string,
  businessTripItemUuid: string,
  body: UpdateTripPlanItemPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/items/${businessTripItemUuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function retryTripPlanItemScheduleSync(
  planUuid: string,
  businessTripItemUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{
    queued: true;
    planItemUuid: string;
    businessTripItemUuid: string;
  }>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/items/${encodeURIComponent(businessTripItemUuid)}/schedule-sync`,
    { method: 'POST', ...(options || {}) },
  );
}

export async function reorderTripPlanItems(
  planUuid: string,
  body: ReorderTripPlanItemsPayload,
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(`${TRIP_PREFIX}/plans/${planUuid}/items/reorder`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function removeTripPlanItem(
  planUuid: string,
  businessTripItemUuid: string,
  body: RemoveTripPlanItemPayload = {},
  options?: ApiRequestOptions,
) {
  return request<TripPlanDetail>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/items/${encodeURIComponent(businessTripItemUuid)}/remove`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}) },
  );
}

export async function trashTripPlanItem(planUuid: string, businessTripItemUuid: string) {
  return request<TripPlanDetail>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/items/${encodeURIComponent(businessTripItemUuid)}/trash`,
    { method: 'PATCH' },
  );
}

export async function restoreTripPlanItem(planUuid: string, businessTripItemUuid: string) {
  return request<TripPlanDetail>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/items/${encodeURIComponent(businessTripItemUuid)}/restore`,
    { method: 'PATCH' },
  );
}

export async function listTripPlanRecycleBin(planUuid: string) {
  return request<TripPlanRecycleBinItem[]>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/recycle-bin`,
    { method: 'GET' },
  );
}

export async function purgeTripPlanRecycleBin(planUuid: string, olderThanThirtyDays = false) {
  return request<{ deletedCount: number }>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/recycle-bin${olderThanThirtyDays ? '/expired' : ''}`,
    { method: 'DELETE' },
  );
}

export async function listTripPlanActivities(
  planUuid: string,
  query?: { businessTripItemUuid?: string; cursor?: string; limit?: number },
) {
  return request<TripPlanActivityPage>(
    `${TRIP_PREFIX}/plans/${encodeURIComponent(planUuid)}/activities`,
    { method: 'GET', params: query },
  );
}

export async function getTripPlanWeatherComparison(
  planUuid: string,
  query: TripWeatherComparisonQuery = {},
  options?: ApiRequestOptions,
) {
  return request<TripWeatherComparisonResult>(`${TRIP_PREFIX}/plans/${planUuid}/weather-comparison`, {
    method: 'GET', params: query, ...(options || {}),
  });
}
